import { useEffect, useMemo } from 'react';
import { ExperienceStateEnum } from '@koob/enums';
import { isExtraOfferedForPeriod, isExtraStateAvailable } from '@koob/utils';
import { useTripExperiencePricing } from '../../hooks/useTripExperiencePricing';
import { priceExperience } from '../../logic/priceExperience';
import {
  getAccommodationExtraIds,
  getInstanceExtras,
} from '../../logic/experienceDayPricing';
import { PRODUCT_AVAILABILITY } from '../../logic/productAvailability';
import { useExperiencePricingConfig } from '../../hooks/useExperiencePricingConfig';
import { useTripDayExperiencePriceRequest } from './useTripDayExperiencePriceRequest';

interface PricingParams {
  dayIndex: number;
  date: unknown;
  experience: any;
  setExperience: (experience: any) => void;
  days: any[];
  tripFilters: any;
  showDayOptions: boolean;
  isExperienceFirstDay: boolean;
  experienceStartIndex: number;
  registerExperienceWarning?: (...args: any[]) => void;
}

// Fetches this instance's pricing, derives its extras/availability, writes
// the shared pricing hook and registers this day's booking verdict with the
// frame. Only meaningful for the day that owns the instance (day 1); other
// days still call it so their own `experienceExtras` stays correct, but their
// verdict/registration effects are no-ops (see `isExperienceFirstDay`).
export function useTripDayExperiencePricing({
  dayIndex,
  date,
  experience,
  setExperience,
  days,
  tripFilters,
  showDayOptions,
  isExperienceFirstDay,
  experienceStartIndex,
  registerExperienceWarning,
}: PricingParams) {
  const { priceObject, priceLoading, requiredMinPax, extrasPrices } =
    useTripDayExperiencePriceRequest({
      dayIndex,
      date,
      experience,
      setExperience,
      tripFilters,
    });

  const accomodationExtras = getAccommodationExtraIds(experience);

  const suggestedExperiences: any[] =
    experience?.programs?.[experience?.dayIndex - 1]?.suggestedExperiences ??
    [];

  const extrasList: any[] = showDayOptions
    ? suggestedExperiences
    : (experience?.extras ?? []).filter(
        (extra: any) => !accomodationExtras.includes(extra.id),
      );

  // KD-3749 — only what can be used on these dates counts as offered: a
  // service the modal leaves out must not be announced here either.
  const dayAvailableCount = suggestedExperiences.filter(
    (extra: any) =>
      isExtraStateAvailable(extra) &&
      isExtraOfferedForPeriod(extrasPrices, extra.id, experience?.dayIndex - 1),
  ).length;

  // Aggregate extras from all days of this multi-day experience
  const experienceExtras = useMemo(
    () => getInstanceExtras(days, experience, experienceStartIndex),
    // `experience` is read whole; its id, length and day index decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      days,
      experience?.id,
      experience?.durationDays,
      experienceStartIndex,
      experience?.dayIndex,
    ],
  );

  // An app's own price rule (DMC: what a previous booking already settled)
  // wins over the fetched one, for the currency shown as much as the price.
  const { getExperiencePriceOverride, storesPricingResponse } =
    useExperiencePricingConfig();
  const priceOverride = getExperiencePriceOverride?.(
    experience,
    experienceExtras,
  );
  const currency = priceOverride?.currency ?? priceObject?.currency;
  const price =
    priceOverride?.price ??
    priceExperience(priceObject, experienceExtras)?.price;

  const isAvailable = experience?.state === ExperienceStateEnum.AVAILABLE;

  // The program cannot be sold as it stands: unpublished, not priceable on
  // these dates, or priced in a currency the trip cannot add up. The pricing
  // request lands on the day, so this day is the only place that knows — hence
  // the warning below, and the report to the frame that disables the booking.
  // Only the first day of an instance decides, the same one that draws it.
  const hasAvailabilityWarning =
    isExperienceFirstDay &&
    !priceLoading &&
    (!isAvailable ||
      (!priceObject && !priceOverride) ||
      isNaN(price as number) ||
      currency === 'mismatch');

  // US-059 — the write itself (day.tripPrice/day.experiencePricing) lives in
  // the shared hook; `price`/`currency` above stay local, they also drive
  // this component's own rendering (the warning triangle, the chip prices).
  // An app that keeps the raw response in `day.experiencePricing` must not
  // have it overwritten with the total.
  useTripExperiencePricing(
    dayIndex,
    priceObject,
    experienceExtras,
    !storesPricingResponse,
  );

  // KD-3643 §3 — while the price is still being fetched the verdict is not
  // known, and an unknown verdict must not let the booking through either. The
  // instance is held pending until the answer lands, without drawing the red
  // triangle: nothing is wrong yet, we simply do not know.
  let experienceAvailabilityStatus: string | null = null;
  if (isExperienceFirstDay) {
    if (priceLoading) {
      experienceAvailabilityStatus = PRODUCT_AVAILABILITY.PENDING;
    } else if (hasAvailabilityWarning) {
      experienceAvailabilityStatus = PRODUCT_AVAILABILITY.UNAVAILABLE;
    }
  }

  // KD-3643 §1 — the effect used to run in every day of the instance. Days 2..n
  // have no verdict to give, yet they wrote one under the shared key and wiped
  // out what day 1 had just reported, so an unavailable multi-day program left
  // the button enabled. Only the day that owns the instance registers, exactly
  // like the removal handler.
  useEffect(() => {
    if (!registerExperienceWarning || !isExperienceFirstDay) {
      return undefined;
    }

    const startDayIndex = experienceStartIndex;
    registerExperienceWarning(startDayIndex, experienceAvailabilityStatus);

    return () => registerExperienceWarning(startDayIndex, null);
  }, [
    registerExperienceWarning,
    isExperienceFirstDay,
    experienceStartIndex,
    experienceAvailabilityStatus,
  ]);

  return {
    priceLoading,
    priceObject,
    extrasPrices,
    currency,
    hasAvailabilityWarning,
    requiredMinPax,
    isCurrencyMismatch: currency === 'mismatch',
    experienceExtras,
    accomodationExtras,
    suggestedExperiences,
    extrasList,
    dayAvailableCount,
  };
}
